/**
 * @fileoverview State and actions for the home calendar widget.
 * @module hooks/useHomeCalendar
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { initializeHomeCalendar, getDayAgendaForDate } from "../logic/homeCalendar";
import {
  buildDayNames,
  buildMonthWeeks,
  formatMonthName,
  isViewingCurrentMonth,
  isViewingNextMonth,
} from "../components/HomeCalendar/homeCalendarDates";
import { EVENTS_ROUTE } from "../components/HomeCalendar/homeCalendarConstants";

const EMPTY_CALENDAR_DATA = {
  events: [],
  businessEvents: [],
  b2bEvents: [],
  breakfastEvents: [],
  virtualEvents: [],
  appointments: [],
  virtualAppointments: [],
};

const MONTH_STEP = 1;

/**
 * Moves the visible month by one calendar month.
 * setMonth rolls the year when the month is January or December.
 * @param {Date} prevDate
 * @param {number} offset
 * @returns {Date}
 */
const shiftMonth = (prevDate, offset) => {
  const newDate = new Date(prevDate);
  newDate.setMonth(prevDate.getMonth() + offset);
  return newDate;
};

/**
 * Values the header, grid, and day dialog read on each render.
 * Day names and the month title follow the active language. The agenda
 * is built only after a day is selected.
 * @param {Object} view
 * @returns {Object}
 */
const buildHomeCalendarState = ({
  currentDate,
  monthDays,
  calendarData,
  isExpanded,
  selectedDay,
  language,
  t,
}) => ({
  currentDate,
  monthDays,
  calendarData,
  isExpanded,
  selectedDay,
  dayNames: buildDayNames(language),
  dayAgenda: selectedDay
    ? getDayAgendaForDate(selectedDay, calendarData, t, language)
    : null,
  monthName: formatMonthName(currentDate, language),
  year: currentDate.getFullYear(),
  isCurrentMonth: isViewingCurrentMonth(currentDate),
  isNextMonth: isViewingNextMonth(currentDate),
  language,
  t,
});

/**
 * Loads the signed-in user's calendar once, then rebuilds the month grid
 * when the visible month or the language changes. Previous and next stay
 * within the current month and the following month.
 * @returns {{state: Object, actions: Object}}
 */
const useHomeCalendar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthDays, setMonthDays] = useState([]);
  const [calendarData, setCalendarData] = useState(EMPTY_CALENDAR_DATA);
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  // Fetch runs only when a user id is stored. There is no loading flag:
  // the grid renders the empty calendar until this request resolves.
  useEffect(() => {
    const fetchCalendarData = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) {
        const data = await initializeHomeCalendar(userId, navigate);
        setCalendarData(data);
      }
    };
    fetchCalendarData();
  }, [navigate]);

  // Language is a dependency so "today" is recalculated on locale change,
  // matching the previous effect even though the grid dates themselves are numeric.
  useEffect(() => {
    setMonthDays(buildMonthWeeks(currentDate));
  }, [currentDate, i18n.language]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const goToPreviousMonth = useCallback(() => {
    setCurrentDate((prevDate) => shiftMonth(prevDate, -MONTH_STEP));
  }, []);

  const goToNextMonth = useCallback(() => {
    setCurrentDate((prevDate) => shiftMonth(prevDate, MONTH_STEP));
  }, []);

  const toggleCalendarExpansion = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const selectDay = useCallback((date) => {
    setSelectedDay(date);
  }, []);

  const closeDayModal = useCallback(() => {
    setSelectedDay(null);
  }, []);

  const openEventsPage = useCallback(() => {
    navigate(EVENTS_ROUTE);
  }, [navigate]);

  const state = buildHomeCalendarState({
    currentDate,
    monthDays,
    calendarData,
    isExpanded,
    selectedDay,
    language: i18n.language,
    t,
  });

  return {
    state,
    actions: {
      goToToday,
      goToPreviousMonth,
      goToNextMonth,
      toggleCalendarExpansion,
      selectDay,
      closeDayModal,
      openEventsPage,
    },
  };
};

export default useHomeCalendar;
